// @flow
import * as React from 'react'
import getBackgroundColor from './utils/getBackgroundColor'
import { ProvidePalette } from '../../utils/withPalette'
import { eventToKey } from '../../utils'
import {
  Container,
  Switch,
  Label,
  Background,
  Toggler,
  ValueText,
} from './elements'
import { SIZES } from './appearance/primary'

// Types
import type { Input as InputProps } from '../../core/BaseField'

export type Appearance = 'primary' | 'inverse' | 'primary-inverse'

export type Props = {
  appearance?: Appearance,
  id?: string,
  activeValue: any,
  inactiveValue: any,
  label?: React.Node,
  activeLabel: React.Node,
  inactiveLabel: React.Node,
  size: 'normal',
  disabled?: boolean,
  color: string,
  reversed?: boolean,
  verbose?: boolean,
  input: InputProps,
}

export type Styles = {|
  $appearance?: Appearance,
  $disabled?: boolean,
  $togglerColor: string,
  $backgroundColor: string,
  $pos: number,
  $isDragging: boolean,
  $hasOutline: boolean,
  $size?: 'normal',
|}

type State = {
  pos: number,
  startX: number,
  isDragging: boolean,
  hasOutline: boolean,
}

/**
 * Wrapper to pass only needed props to styled component
 * @return {React.Element}
 */
export class SwitchField extends React.Component<Props, State> {
  static defaultProps = {
    appearance: 'primary',
    size: 'normal',
    disabled: false,
    color: 'PRIMARY',
    activeLabel: 'Yes',
    inactiveLabel: 'No',
    activeValue: true,
    inactiveValue: false,
  }

  handleDiameter: number

  checkedPos: number

  uncheckedPos: number

  constructor(props: Props) {
    super(props)

    const { size, reversed } = this.props
    const { height, width, handleDiameter } = SIZES[size]

    this.handleDiameter = handleDiameter || height - 2
    this.checkedPos = Math.max(
      width - height,
      width - (height + this.handleDiameter) / 2
    )
    this.uncheckedPos = Math.max(0, (height - this.handleDiameter) / 2)

    const value = this.checked()

    const isChecked = reversed ? !value : value

    this.state = {
      pos: isChecked ? this.checkedPos : this.uncheckedPos,
      startX: 0,
      isDragging: false,
      hasOutline: false,
    }
  }

  checked(props?: Props) {
    const {
      input: { value },
      activeValue,
    } = props || this.props

    return value === activeValue
  }

  get nextValue(): any {
    const { activeValue, inactiveValue } = this.props

    const value = this.checked() ? inactiveValue : activeValue

    return value
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { reversed } = nextProps

    const value = this.checked(nextProps)

    const isChecked = reversed ? !value : value

    const pos = isChecked ? this.checkedPos : this.uncheckedPos
    this.setState({ pos })
  }

  handleDragStart = (clientX: number) => {
    this.setState({ startX: clientX, hasOutline: true })
  }

  handleDrag = (clientX: number) => {
    const { startX } = this.state

    const startPos = this.checked() ? this.checkedPos : this.uncheckedPos
    const newPos = startPos + clientX - startX
    const pos = Math.min(this.checkedPos, Math.max(this.uncheckedPos, newPos))
    this.setState({ pos, isDragging: true })
  }

  handleDragStop = (event: Object) => {
    const { pos, isDragging } = this.state

    const {
      input: { onChange },
      id,
      activeValue,
      inactiveValue,
    } = this.props

    // Simulate clicking the handle
    if (!isDragging) {
      this.setState({ startX: 0, hasOutline: false })
      onChange && onChange(this.nextValue, event, id)
      return
    }
    if (this.checked()) {
      if (pos > (this.checkedPos + this.uncheckedPos) / 2) {
        this.setState({
          pos: this.checkedPos,
          startX: 0,
          isDragging: false,
          hasOutline: false,
        })
        return
      }
      this.setState({ startX: 0, isDragging: false, hasOutline: false })
      onChange && onChange(inactiveValue, event, id)
      return
    }
    if (pos < (this.checkedPos + this.uncheckedPos) / 2) {
      this.setState({
        pos: this.uncheckedPos,
        startX: 0,
        isDragging: false,
        hasOutline: false,
      })
      return
    }
    this.setState({ startX: 0, isDragging: false, hasOutline: false })
    onChange && onChange(activeValue, event, id)
  }

  handleMouseDown = (event: Object) => {
    // Ignore right click and scroll
    if (typeof event.button === 'number' && event.button !== 0) {
      return
    }

    this.handleDragStart(event.clientX)
    document.addEventListener('mousemove', this.handleMouseMove)
    document.addEventListener('mouseup', this.handleMouseUp)
  }

  handleMouseMove = (event: Object) => {
    event.preventDefault()
    this.handleDrag(event.clientX)
  }

  handleMouseUp = (event: Object) => {
    this.handleDragStop(event)
    document.removeEventListener('mousemove', this.handleMouseMove)
    document.removeEventListener('mouseup', this.handleMouseUp)
  }

  handleTouchStart = (event: Object) => {
    this.handleDragStart(event.touches[0].clientX)
  }

  handleTouchMove = (event: Object) => {
    this.handleDrag(event.touches[0].clientX)
  }

  handleTouchEnd = (event: Object) => {
    event.preventDefault()
    this.handleDragStop(event)
  }

  handleTouchCancel = () => {
    this.setState({ startX: 0, hasOutline: false })
  }

  handleClick = (event: Object) => {
    const {
      input: { onChange },
      id,
    } = this.props

    onChange && onChange(this.nextValue, event, id)
  }

  handleKeyDown = (event: Object) => {
    const {
      input: { onChange },
      id,
    } = this.props

    const { isDragging } = this.state

    const key = eventToKey(event)
    // Trigger change on spacebar and enter keys (in violation of wai-aria spec).
    if ((key === 'Enter' || key === 'Space') && !isDragging) {
      event.preventDefault()
      onChange && onChange(this.nextValue, event, id)
    }
  }

  handleFocus = () => {
    const {
      input: { onFocus },
    } = this.props

    onFocus && onFocus()

    this.setState({ hasOutline: true })
  }

  handleBlur = () => {
    const {
      input: { onBlur },
    } = this.props

    onBlur && onBlur()

    this.setState({ hasOutline: false })
  }

  renderWithPalette = ($palette: any) => {
    const {
      appearance,
      size,
      disabled,
      color,
      verbose,
      label,
      activeLabel,
      inactiveLabel,
      id,
    } = this.props

    const { pos, isDragging, hasOutline } = this.state

    const offColor = $palette('GRAY').darken(33).toString()
    const onColor = $palette(color).toString()
    const offHandleColor = $palette('WHITE').toString()
    const onHandleColor = $palette(color).lighten(10).toString()

    const backgroundColor = getBackgroundColor(
      pos,
      this.checkedPos,
      this.uncheckedPos,
      offColor,
      onColor
    )

    const togglerColor = getBackgroundColor(
      pos,
      this.checkedPos,
      this.uncheckedPos,
      offHandleColor,
      onHandleColor
    )

    const styleProps: Styles = {
      $appearance: appearance,
      $size: size,
      $disabled: disabled,
      $backgroundColor: backgroundColor,
      $togglerColor: togglerColor,
      $isDragging: isDragging,
      $hasOutline: hasOutline,
      $pos: pos,
    }

    return (
      <Container {...styleProps}>
        {label && (
          <Label htmlFor={id} {...styleProps}>
            {label}
          </Label>
        )}
        <Switch tabIndex="-1" {...styleProps}>
          <Background
            {...styleProps}
            onClick={disabled ? null : this.handleClick}
          />
          <Toggler
            id={id}
            role="switch"
            type="button"
            aria-checked={this.checked()}
            tabIndex={disabled ? null : 0}
            onMouseDown={disabled ? null : this.handleMouseDown}
            onTouchStart={disabled ? null : this.handleTouchStart}
            onTouchMove={disabled ? null : this.handleTouchMove}
            onTouchEnd={disabled ? null : this.handleTouchEnd}
            onTouchCancel={disabled ? null : this.handleTouchCancel}
            onKeyDown={this.handleKeyDown}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            {...styleProps}
          />
          {verbose && (
            <ValueText {...styleProps}>
              {this.checked() ? activeLabel : inactiveLabel}
            </ValueText>
          )}
        </Switch>
        <input
          type="checkbox"
          aria-hidden="true"
          tabIndex="-1"
          value="on"
          style={{
            transform: 'translateX(-100%)',
            position: 'absolute',
            pointerEvents: 'none',
            opacity: 0,
          }}
          checked={this.checked()}
          onChange={() => {}}
        ></input>
      </Container>
    )
  }

  render() {
    return <ProvidePalette render={this.renderWithPalette} />
  }
}

export default SwitchField
